import type { UnpackedGraphemeStrokes } from "#grapheme-strokes";
import { isPlainObject } from "@-/com.npmjs.is-what";
import { parseStrokeMedianPolyline } from "#parse-stroke-median-polyline";
import { typedArrayLength } from "@-/com.npmjs.typed-array-length";
import {
  decodeSequentialDeltas,
  encodeSequentialDeltas,
  flattenSegmentNums,
  parseSvgPathToAbsoluteRoundedSegments,
  segmentCmdAsciiList,
  svgPathSegmentsToD,
  unflattenSegmentNums,
  type SvgPathSegment,
} from "#svg-path-codec";
import { strFromU8, strToU8, unzlibSync, zlibSync } from "@-/com.npmjs.fflate";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import arrayLength from "@-/com.npmjs.extra-array__length";

const ZLIB_LEVEL = 9 as const;

/** One stroke: `[pathCmdAsciiList, pathNumericDeltas, medianXYDeltas]` (median deltas are sequential x,y deltas). */
export type HeavyStrokeWire = [number[], number[], number[]];

export type HeavyGlyphWire = HeavyStrokeWire[];

const CHUNK_WIRE_VERSION = "han-grapheme-stroke-chunk-v1" as const;

type GraphemeStrokeChunkEntry = {
  glyph: string;
  wire: HeavyGlyphWire;
};

type GraphemeStrokeChunkWire = [
  typeof CHUNK_WIRE_VERSION,
  string[],
  number[],
  number[],
  number[],
  number[],
  number[],
  number[],
  number[],
];

function argCountForSvgCommand(cmd: string): number {
  switch (cmd) {
    case "M":
    case "m": {
      return 2;
    }
    case "L":
    case "l": {
      return 2;
    }
    case "H":
    case "h": {
      return 1;
    }
    case "V":
    case "v": {
      return 1;
    }
    case "Q":
    case "q": {
      return 4;
    }
    case "C":
    case "c": {
      return 6;
    }
    case "Z":
    case "z": {
      return 0;
    }
    default: {
      return 0;
    }
  }
}

function emptySegmentsFromCmdAsciiList(cmdAsciiList: number[]): SvgPathSegment[] {
  return cmdAsciiList.map((code) => {
    const cmd = String.fromCodePoint(code);
    const n = argCountForSvgCommand(cmd);
    const nums = Array.from<number>({ length: n }).fill(0);
    return { cmd, nums };
  });
}

function medianPairsToDeltaFlat(medians: [number, number][]): number[] {
  const flat: number[] = [];
  for (const [x, y] of medians) {
    flat.push(x, y);
  }
  return encodeSequentialDeltas(flat);
}

function deltaFlatToMedianPairs(flatDeltas: number[]): [number, number][] {
  const abs = decodeSequentialDeltas(flatDeltas);
  const pairs: [number, number][] = [];
  for (let i = 0; i + 1 < arrayLength(abs); i += 2) {
    const x = abs[i];
    const y = abs[i + 1];
    if (x === undefined || y === undefined) {
      break;
    }
    pairs.push([x, y]);
  }
  return pairs;
}

function encodeStroke(path: string, medianUnknown: unknown): HeavyStrokeWire | null {
  const segments = parseSvgPathToAbsoluteRoundedSegments(path);
  if (hasLength(segments, 0)) {
    return null;
  }
  const medians = parseStrokeMedianPolyline(medianUnknown);
  if (hasLength(medians, 0)) {
    return null;
  }
  const cmdAscii = segmentCmdAsciiList(segments);
  const flatNums = flattenSegmentNums(segments);
  const pathDeltas = encodeSequentialDeltas(flatNums);
  const medianDeltas = medianPairsToDeltaFlat(medians);
  return [cmdAscii, pathDeltas, medianDeltas];
}

function decodeStroke(wire: HeavyStrokeWire): UnpackedGraphemeStrokes | null {
  const [cmdAscii, pathDeltas, medianDeltas] = wire;
  if (!Array.isArray(cmdAscii) || !Array.isArray(pathDeltas) || !Array.isArray(medianDeltas)) {
    return null;
  }
  if (!cmdAscii.every((n) => typeof n === "number")) {
    return null;
  }
  if (
    !pathDeltas.every((n) => typeof n === "number") ||
    !medianDeltas.every((n) => typeof n === "number")
  ) {
    return null;
  }
  const template = emptySegmentsFromCmdAsciiList(cmdAscii);
  const expectedFlat = template.reduce((s, seg) => s + arrayLength(seg.nums), 0);
  const flatNums = decodeSequentialDeltas(pathDeltas);
  if (!hasLength(flatNums, expectedFlat)) {
    return null;
  }
  const filled = unflattenSegmentNums(template, flatNums);
  if (!hasLength(filled, arrayLength(template))) {
    return null;
  }
  const path = svgPathSegmentsToD(filled);
  const medians = deltaFlatToMedianPairs(medianDeltas);
  return { medians, path };
}

function isNumberList(value: unknown): value is number[] {
  return Array.isArray(value) && value.every((n) => typeof n === "number" && Number.isFinite(n));
}

function isHeavyStrokeWire(value: unknown): value is HeavyStrokeWire {
  if (!Array.isArray(value) || !hasLength(value, 3)) {
    return false;
  }
  return isNumberList(value[0]) && isNumberList(value[1]) && isNumberList(value[2]);
}

function isHeavyGlyphWire(value: unknown): value is HeavyGlyphWire {
  return Array.isArray(value) && value.every(isHeavyStrokeWire);
}

function encodeGlyphWire(wire: HeavyGlyphWire): Uint8Array {
  return zlibSync(strToU8(JSON.stringify(wire)), { level: ZLIB_LEVEL });
}

function getStrokeWire(hanziWriterCharacter: unknown): HeavyGlyphWire | null {
  if (!isPlainObject(hanziWriterCharacter)) {
    return null;
  }
  const strokePathList = hanziWriterCharacter.strokes;
  const medianList = hanziWriterCharacter.medians;
  if (!Array.isArray(strokePathList) || !Array.isArray(medianList)) {
    return null;
  }
  const strokeCount = Math.min(arrayLength(strokePathList), arrayLength(medianList));
  const wires: HeavyGlyphWire = [];
  for (let i = 0; i < strokeCount; i++) {
    const strokePath = strokePathList[i];
    if (typeof strokePath !== "string" || strokePath.length === 0) {
      continue;
    }
    const w = encodeStroke(strokePath, medianList[i]);
    if (w !== null) {
      wires.push(w);
    }
  }
  return hasLength(wires, 0) ? null : wires;
}

function decodeGlyphWire(wire: HeavyGlyphWire): UnpackedGraphemeStrokes[] | null {
  const out: UnpackedGraphemeStrokes[] = [];
  for (const item of wire) {
    const decoded = decodeStroke(item);
    if (decoded === null) {
      return null;
    }
    out.push(decoded);
  }
  return out;
}

function parseGlyphWireFromBlob(blob: Uint8Array): HeavyGlyphWire | null {
  if (typedArrayLength(blob) === 0) {
    return null;
  }
  let inflated: Uint8Array;
  try {
    inflated = unzlibSync(blob);
  } catch {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(strFromU8(inflated));
  } catch {
    return null;
  }
  return isHeavyGlyphWire(parsed) ? parsed : null;
}

/**
 * Packs an unpacked stroke list (same shape as {@link expandPackedStrokes} output) using the
 * same wire format as {@link getPackedStrokes} on Hanzi Writer JSON with matching `strokes` /
 * `medians` arrays.
 */
export function getPackedStrokesFromUnpackedStrokeList(
  strokeList: UnpackedGraphemeStrokes[],
): Uint8Array | null {
  if (hasLength(strokeList, 0)) {
    return null;
  }
  return getPackedStrokes({
    medians: strokeList.map((s) => s.medians),
    strokes: strokeList.map((s) => s.path),
  });
}

/**
 * Packs Hanzi Writer character JSON into a small zlib blob: per stroke, SVG `d` is split into
 * command ASCII codes plus sequentially delta-encoded rounded coordinates; medians are
 * `x,y,x,y,…` with the same sequential delta encoding.
 */
export function getPackedStrokes(hanziWriterCharacter: unknown): Uint8Array | null {
  const wire = getStrokeWire(hanziWriterCharacter);
  return wire === null ? null : encodeGlyphWire(wire);
}

/** Expands a blob from {@link getPackedStrokes} into stroke shapes (rounded path + integer medians). */
export function expandPackedStrokes(blob: Uint8Array): UnpackedGraphemeStrokes[] | null {
  const wire = parseGlyphWireFromBlob(blob);
  return wire === null ? null : decodeGlyphWire(wire);
}

/** Returns the validated uncompressed wire stored inside a per-glyph packed stroke blob. */
export function getPackedStrokeWireFromBlob(blob: Uint8Array): HeavyGlyphWire | null {
  return parseGlyphWireFromBlob(blob);
}

/** Packs many glyph stroke wires into one zlib chunk optimized for cross-glyph compression. */
export function encodeGraphemeStrokeChunk(entries: GraphemeStrokeChunkEntry[]): Uint8Array {
  const glyphList: string[] = [];
  const strokeCountList: number[] = [];
  const cmdCountList: number[] = [];
  const pathDeltaCountList: number[] = [];
  const medianDeltaCountList: number[] = [];
  const cmdAsciiList: number[] = [];
  const pathDeltaList: number[] = [];
  const medianDeltaList: number[] = [];

  for (const { glyph, wire } of entries) {
    glyphList.push(glyph);
    strokeCountList.push(arrayLength(wire));

    for (const [cmdAscii, pathDeltas, medianDeltas] of wire) {
      cmdCountList.push(arrayLength(cmdAscii));
      pathDeltaCountList.push(arrayLength(pathDeltas));
      medianDeltaCountList.push(arrayLength(medianDeltas));
      cmdAsciiList.push(...cmdAscii);
      pathDeltaList.push(...pathDeltas);
      medianDeltaList.push(...medianDeltas);
    }
  }

  const chunkWire: GraphemeStrokeChunkWire = [
    CHUNK_WIRE_VERSION,
    glyphList,
    strokeCountList,
    cmdCountList,
    pathDeltaCountList,
    medianDeltaCountList,
    cmdAsciiList,
    pathDeltaList,
    medianDeltaList,
  ];

  return zlibSync(strToU8(JSON.stringify(chunkWire)), { level: ZLIB_LEVEL });
}

/** Decodes a zlib chunk produced by {@link encodeGraphemeStrokeChunk}. */
export function decodeGraphemeStrokeChunk(
  blob: Uint8Array,
): ReadonlyMap<string, UnpackedGraphemeStrokes[]> | null {
  let inflated: Uint8Array;
  try {
    inflated = unzlibSync(blob);
  } catch {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(strFromU8(inflated));
  } catch {
    return null;
  }

  if (!Array.isArray(parsed) || !hasLength(parsed, 9) || parsed[0] !== CHUNK_WIRE_VERSION) {
    return null;
  }

  const glyphList = parsed[1];
  const strokeCountList = parsed[2];
  const cmdCountList = parsed[3];
  const pathDeltaCountList = parsed[4];
  const medianDeltaCountList = parsed[5];
  const cmdAsciiList = parsed[6];
  const pathDeltaList = parsed[7];
  const medianDeltaList = parsed[8];

  if (
    !Array.isArray(glyphList) ||
    !glyphList.every((glyph) => typeof glyph === "string") ||
    !isNumberList(strokeCountList) ||
    !isNumberList(cmdCountList) ||
    !isNumberList(pathDeltaCountList) ||
    !isNumberList(medianDeltaCountList) ||
    !isNumberList(cmdAsciiList) ||
    !isNumberList(pathDeltaList) ||
    !isNumberList(medianDeltaList)
  ) {
    return null;
  }

  const decodedByGlyph = new Map<string, UnpackedGraphemeStrokes[]>();
  let strokeCursor = 0;
  let cmdCursor = 0;
  let pathCursor = 0;
  let medianCursor = 0;

  for (const [glyphIndex, element] of glyphList.entries()) {
    const glyph = element;
    const strokeCount = strokeCountList[glyphIndex];
    if (strokeCount === undefined) {
      return null;
    }

    const wire: HeavyGlyphWire = [];
    for (let i = 0; i < strokeCount; i++) {
      const cmdCount = cmdCountList[strokeCursor];
      const pathCount = pathDeltaCountList[strokeCursor];
      const medianCount = medianDeltaCountList[strokeCursor];
      if (cmdCount === undefined || pathCount === undefined || medianCount === undefined) {
        return null;
      }
      wire.push([
        cmdAsciiList.slice(cmdCursor, cmdCursor + cmdCount),
        pathDeltaList.slice(pathCursor, pathCursor + pathCount),
        medianDeltaList.slice(medianCursor, medianCursor + medianCount),
      ]);
      cmdCursor += cmdCount;
      pathCursor += pathCount;
      medianCursor += medianCount;
      strokeCursor += 1;
    }

    const decoded = decodeGlyphWire(wire);
    if (decoded === null) {
      return null;
    }
    decodedByGlyph.set(glyph, decoded);
  }

  if (
    !hasLength(cmdCountList, strokeCursor) ||
    !hasLength(pathDeltaCountList, strokeCursor) ||
    !hasLength(medianDeltaCountList, strokeCursor) ||
    !hasLength(cmdAsciiList, cmdCursor) ||
    !hasLength(pathDeltaList, pathCursor) ||
    !hasLength(medianDeltaList, medianCursor)
  ) {
    return null;
  }

  return decodedByGlyph;
}
